import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  DataTable,
  DataTableCellVariants,
  DataTableHeaderProps,
  DataTableRowData,
  EmptyState,
  RowsSkeletonLoader,
  useSidePanel,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/ThemeLottie'
import { DateFormat } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useApprovalsTransactionsQuery } from 'hooks/queries/useApprovalsTransactionsQuery'
import { ApprovalTableTypes } from 'pages/Approvals/types'
import { getProcessedByDetails, getTableHeaders } from 'pages/Approvals/util'
import { ApprovalActionStatuses, ApprovalStatuses } from 'types/approvals'
import { SidePanelSummary } from '../SidePanel/SidePanelSummary'

export const RejectedTransactions = () => {
  const { t } = useTranslation()
  const { currentAccount } = useAccountsContext()

  const currentAccountReference = currentAccount?.reference || ''
  const {
    data: rejectedTransactions,
    isError,
    isFetching,
  } = useApprovalsTransactionsQuery(
    currentAccountReference,
    ApprovalStatuses.REJECTED,
    !!currentAccountReference
  )

  const {
    isSidePanelOpen: isRejectedTransactionDetailsOpen,
    sidePanelData: rejectedTransactionDetails,
    paging,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel(rejectedTransactions)

  const openSidePanelSummary = (index: number) => {
    updateSidePanel(index)
    setIsSidePanelOpen(true)
  }

  const columns: DataTableHeaderProps[] = useMemo(() => {
    return getTableHeaders(t, ApprovalTableTypes.REJECTED)
  }, [t])

  const tableData: DataTableRowData[] = useMemo(() => {
    if (!rejectedTransactions) return []

    return rejectedTransactions?.map((approvalTransaction, index) => {
      const { isProcessedByMultiple, isProcessedBySingle, processedByText } = getProcessedByDetails(
        approvalTransaction?.processedBy,
        ApprovalActionStatuses.PAYOUT_MERCHANT_APPROVAL_REJECTED,
        t
      )

      return {
        id: approvalTransaction?.correlationKey,
        testid: approvalTransaction?.correlationKey,
        onRowClick: () => {
          openSidePanelSummary(index)
        },
        cells: [
          {
            key: 'payment',
            variant: DataTableCellVariants.TEXT,
            Avatar: <Avatar size="small" name={approvalTransaction?.currency?.code} />,
            Text: `${approvalTransaction?.amount} ${approvalTransaction?.currency?.code}`,
            BottomText: approvalTransaction?.walletName,
          },
          {
            key: 'to',
            variant: DataTableCellVariants.TEXT,
            Text:
              approvalTransaction?.beneficiaryDetails?.accountNumber ||
              approvalTransaction?.beneficiaryDetails?.destinationWallet,
            BottomText: approvalTransaction?.beneficiaryDetails?.beneficiaryName,
          },
          {
            key: 'initiatedBy',
            variant: DataTableCellVariants.TEXT,
            Text: approvalTransaction?.initiatedBy || approvalTransaction?.initiatedByEmail,
            BottomText: dayjs(approvalTransaction?.actionedAt).format(
              DateFormat.D_MMM_YYYY_AT_HH_MM
            ),
          },
          {
            key: 'rejectedBy',
            variant: DataTableCellVariants.TEXT,
            ...(isProcessedBySingle && {
              Text:
                approvalTransaction?.processedBy[0]?.userFullName ??
                approvalTransaction?.processedBy[0]?.email,
              BottomText: dayjs(approvalTransaction?.actionedAt).format(
                DateFormat.D_MMM_YYYY_AT_HH_MM
              ),
            }),
            ...(isProcessedByMultiple && {
              Text: processedByText,
            }),
            ...(!isProcessedBySingle &&
              !isProcessedByMultiple && {
                Text: approvalTransaction?.actionedBy,
                BottomText: dayjs(approvalTransaction?.actionedAt).format(
                  DateFormat.D_MMM_YYYY_AT_HH_MM
                ),
              }),
          },
          {
            key: 'reasonForRejection',
            variant: DataTableCellVariants.TEXT,
            Text: approvalTransaction?.rejectionReason,
          },
        ],
      }
    })
  }, [rejectedTransactions])

  return (
    <Box paddingB={16}>
      {isFetching && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

      {!isFetching && isError && (
        <EmptyState
          testId="error-state"
          LottiePlayer={<Lottie src="connection-error" />}
          title={t('oopsSomethingWentWrong')}
        />
      )}

      {!isFetching && !isError && !tableData?.length && (
        <EmptyState
          testId="empty-state"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('approvals.emptyStateHeadline', {
            approvalStatus: ApprovalStatuses.REJECTED.toLowerCase(),
          })}
          description={t('approvals.emptyStateDescription', {
            approvalStatus: ApprovalStatuses.REJECTED.toLowerCase(),
          })}
        />
      )}

      {!isFetching && !isError && !!tableData?.length && (
        <>
          <DataTable name="rejected-transactions-table" headers={columns} data={tableData} />
          {rejectedTransactionDetails && (
            <SidePanelSummary
              isOpen={isRejectedTransactionDetailsOpen}
              paging={paging}
              onClose={() => setIsSidePanelOpen(false)}
              transactionDetails={rejectedTransactionDetails}
              tab={ApprovalStatuses.REJECTED}
            />
          )}
        </>
      )}
    </Box>
  )
}
